/**
 * Typed access to Vite env. All public env vars must start with VITE_.
 *
 * Hosting model: the portal answers on several domains (mercaito.dev,
 * portal-v2.ismstorage.work) and reaches the API through whatever serves it —
 * the Vite dev server locally, the container's nginx in production. So
 * `apiBase` is normally EMPTY and every request is same-origin.
 *
 *   - apiBase is the API ORIGIN (no `/api` suffix). Path prefixes like
 *     `/api/v3/...` or `/api/SP_Common/...` are passed as-is to buildUrl().
 *   - Setting VITE_API_BASE_URL at build time points the bundle straight at an
 *     API host instead. That only works where the API grants CORS to the
 *     portal's origin, which it does by a hand-written allow-list — the reason
 *     the proxy exists.
 */
export const env = {
  apiBase: (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '',
  appName: import.meta.env.VITE_APP_NAME ?? 'MERCAITO',
  appVersion: __APP_VERSION__,
  buildTime: __BUILD_TIME__,
  turnstileSiteKey:
    import.meta.env.VITE_TURNSTILE_SITE_KEY ?? '0x4AAAAAAC-cMLwgo422zEUa',
  imageFallbackBase:
    (import.meta.env.VITE_IMAGE_FALLBACK_BASE as string | undefined) ?? '',
  /** Substituted into the privacy notice, which keeps it as a placeholder. */
  supportEmail:
    (import.meta.env.VITE_SUPPORT_EMAIL as string | undefined) ?? 'support@isellmore.com',
} as const;

/** `/archive/32/<key>` or `/storage/<key>` → the S3 key. */
const ARCHIVE_KEY_RE = /\/(?:archive|storage)\/(?:\d+\/)?(.+)$/i;

/** First key segment is the SQL database name: `saleit_db_dev_foo` → `saleit_db_foo`. */
const ENV_DB_PREFIX_RE = /^saleit_db_(?:dev|test|qa|qatest|release)_/i;

/**
 * TEMPORARY, non-prod only: serve photos from the production S3 bucket.
 *
 * Non-prod databases are restores of production, so their photo rows point at
 * objects that were never copied into the per-environment bucket
 * (`ism-eu-central-2-dev` and friends) — and dev has no `/s3proxy/` route at
 * all, so `/archive/…` dead-ends in the legacy SPA's index.html and every tile
 * renders as alt text.  With `VITE_IMAGE_FALLBACK_BASE` set we skip the
 * environment's own storage and read the object straight from the public
 * proxy on a prod node, stripping the `dev_`/`qatest_` infix that the restore
 * added to the database name.
 *
 * Retire this once dev serves `/s3proxy/` itself: photos actually taken on dev
 * exist only in the dev bucket and are invisible while the switch is on.
 */
function applyImageFallback(url: string): string {
  if (!env.imageFallbackBase) return url;
  const key = ARCHIVE_KEY_RE.exec(url)?.[1];
  if (!key) return url;
  return `${env.imageFallbackBase.replace(/\/+$/, '')}/${key.replace(ENV_DB_PREFIX_RE, 'saleit_db_')}`;
}

/**
 * Resolve a backend-relative asset path (e.g. `archive/31/.../0.jpg`) into an
 * absolute URL pointing at the API origin (`env.apiBase`).
 *
 * - Returns `undefined` for empty input so callers can spread it into
 *   `<img src={…}>` without rendering a broken image.
 * - Pass-through for fully-qualified URLs (`http(s)://…`).
 * - Same-origin (apiBase === '') falls back to a leading slash so the
 *   browser resolves against the current document — useful in dev when the
 *   Vite proxy serves /archive/* alongside /api/*.
 *
 * Used by `photo-viewer` thumbnails today; will be reused by the fullscreen
 * viewer and any future asset-rendering pages once Wave 1C lands.
 *
 * When `VITE_IMAGE_FALLBACK_BASE` is set, archive URLs are additionally
 * redirected to the production object store — see `applyImageFallback`.
 */
export function absolutizeBackendUrl(path?: string | null): string | undefined {
  if (!path) return undefined;
  if (/^https?:\/\//i.test(path)) return applyImageFallback(path);
  const cleaned = path.replace(/^\/+/, '');
  if (!env.apiBase) return applyImageFallback(`/${cleaned}`);
  return applyImageFallback(`${env.apiBase.replace(/\/+$/, '')}/${cleaned}`);
}
